import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom'; 
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  CircularProgress,
  Rating,
  IconButton,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChatIcon from '@mui/icons-material/Chat';
import PhoneIcon from '@mui/icons-material/Phone';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Footer from '../../components/common/Footer';
import { fetchLabs } from '../../services/home';

// Theme Colors
const themeColors = {
  primary: '#31C48D',
  secondary: '#A1E06D',
  background: '#F9FAFB',
  white: '#FFFFFF',
  grey: '#686978',
  lightGrey: '#F1F1F1',
  darkGrey: '#4D4D4D',
};

// Horizontal Scroll Component
const HorizontalScroll = ({ children }) => {
  const scrollContainer = React.useRef(null);

  const scroll = (direction) => {
    const container = scrollContainer.current;
    const scrollAmount = direction === 'left' ? -300 : 300;
    container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  };

  return (
    <Box sx={{ position: 'relative', marginTop: '20px' }}>
      <IconButton
        onClick={() => scroll('left')}
        sx={{
          position: 'absolute',
          left: -20,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
          backgroundColor: themeColors.white,
          boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
        }}
      >
        <ArrowBackIosIcon />
      </IconButton>

      <Box
        ref={scrollContainer}
        sx={{
          display: 'flex',
          overflowX: 'auto',
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': { display: 'none' },
          padding: '10px',
        }}
      >
        {children}
      </Box>

      <IconButton
        onClick={() => scroll('right')}
        sx={{
          position: 'absolute',
          right: -20,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
          backgroundColor: themeColors.white,
          boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
        }}
      >
        <ArrowForwardIosIcon />
      </IconButton>
    </Box>
  );
};

// Lab Card Component
// Import useNavigate
const LabCard = ({ lab }) => {
  const navigate = useNavigate(); // React Router hook for navigation

  // Navigate to Lab Detail Screen
  const handleNavigate = () => {
    navigate(`/labs/${lab.reference_id}`); // Pass lab ID in the route
  };

  return (
    <Card
      sx={{
        minWidth: '320px',
        marginRight: '16px',
        borderRadius: '16px',
        boxShadow: '0 6px 15px rgba(0,0,0,0.1)', // Soft shadow
        background: `linear-gradient(135deg, ${themeColors.white}, ${themeColors.lightGrey})`, // Subtle gradient
        display: 'flex',
        flexDirection: 'column',
        height: '320px',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-5px)', // Slight lift on hover
          boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
        },
      }}
    >
      <CardContent sx={{ flex: 1 }}>
        {/* Title */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 'bold',
            marginBottom: '8px',
            color: themeColors.darkGrey,
          }}
        >
          {lab.name}
        </Typography>

        {/* Tests */}
        <Chip
          label={lab.total_tests}
          size="small"
          sx={{
            backgroundColor: themeColors.primary,
            color: themeColors.white,
            marginBottom: '8px',
            fontWeight: '500',
          }}
        />

        {/* Address */}
        <Typography
          variant="body2"
          sx={{
            marginBottom: '8px',
            color: themeColors.grey,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {lab.address}
        </Typography>

        {/* Distance */}
        <Typography
          variant="body2"
          sx={{
            marginBottom: '8px',
            color: themeColors.primary,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <LocationOnIcon
            fontSize="small"
            sx={{ marginRight: '4px', color: themeColors.primary }}
          />
          {lab.distance.value} {lab.distance.unit} away from you
        </Typography>
      </CardContent>

      {/* Fixed Book Now Button */}
      <Box
        sx={{
          padding: '8px',
          backgroundColor: themeColors.white,
          borderTop: '1px solid #E0E0E0',
          borderRadius: '0 0 16px 16px',
        }}
      >
        <Button
          variant="contained"
          size="medium"
          onClick={handleNavigate} // Navigate to Lab Detail Screen
          sx={{
            backgroundColor: themeColors.primary,
            color: themeColors.white,
            width: '100%',
            fontWeight: 'bold',
            textTransform: 'none',
            borderRadius: '8px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              backgroundColor: themeColors.primary,
              boxShadow: '0 6px 12px rgba(0, 0, 0, 0.2)',
            },
          }}
        >
          Book Now
        </Button>
      </Box>
    </Card>
  );
};

// Home Section Component
const HomeSection = () => {
  const [labs, setLabs] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch Labs and Reviews
  useEffect(() => {
    const loadLabs = async () => {
      setLoading(true);
      try {
        const data = await fetchLabs(26.8505899, 75.7909157);
        setLabs(data.data.nearby_labs || []);
        setReviews(data.data.reviews || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadLabs();
  }, []);

  return (
    <Box sx={{ backgroundColor: themeColors.background, minHeight: '100vh', padding: '20px' }}>
      {/* Call/Chat Section */}
      <Card
        sx={{
          padding: '16px',
          marginBottom: '20px',
          borderRadius: '12px',
          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
          background: 'linear-gradient(135deg, #31C48D 30%, #3596CD 90%)',
          color: themeColors.white,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Call Or Chat with A Health Expert
        </Typography>
        <Typography variant="body2" sx={{ marginBottom: '16px' }}>
          Need Help? Talk to our health Experts
        </Typography>
        <Box sx={{ display: 'flex', gap: '10px' }}>
          <Button variant="contained" startIcon={<PhoneIcon />} sx={{ backgroundColor: themeColors.white, color: themeColors.primary }}>
            Call Now
          </Button>
          <Button variant="outlined" startIcon={<ChatIcon />} sx={{ color: themeColors.white, borderColor: themeColors.white }}>
            Chat With Us
          </Button>
        </Box>
      </Card>

      {/* Labs Section */}
      <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: '16px' }}>
        Labs Near You
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <HorizontalScroll>
          {labs.map((lab) => (
            <LabCard key={lab.reference_id} lab={lab} />
          ))}
        </HorizontalScroll>
      )}

      {/* Reviews Section */}
      <Typography variant="h6" sx={{ fontWeight: 'bold', marginTop: '20px', marginBottom: '16px' }}>
        Customer Reviews
      </Typography>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <ReviewsScroll reviews={reviews} />
      )}

      <Footer />
    </Box>
  );
};



const ReviewCard = ({ review }) => {
  return (
    <Card
      sx={{
        minWidth: '280px',
        marginRight: '16px',
        borderRadius: '16px',
        boxShadow: '0 6px 15px rgba(0,0,0,0.1)',
        background: themeColors.white,
        display: 'flex',
        flexDirection: 'column',
        padding: '16px',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
        },
      }}
    >
      {/* User Avatar and Name */}
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
        <Avatar sx={{ marginRight: '10px', backgroundColor: themeColors.primary }}>
          {review.name.charAt(0).toUpperCase()}
        </Avatar>
        <Box>
          <Typography variant="body1" fontWeight="bold">
            {review.name}
          </Typography>
          <Rating value={review.rating} readOnly size="small" />
        </Box>
      </Box>

      {/* Review Text */}
      <Typography variant="body2" sx={{ color: themeColors.grey, marginBottom: '8px' }}>
        {review.review}
      </Typography>

      {/* Location */}
      <Typography
        variant="caption"
        sx={{ color: themeColors.primary, display: 'flex', alignItems: 'center' }}
      >
        <LocationOnIcon fontSize="small" sx={{ marginRight: '4px' }} />
        {review.location}
      </Typography>
    </Card>
  );
};

// Reviews Horizontal Scroll
const ReviewsScroll = ({ reviews }) => {
  const scrollContainer = React.useRef(null);

  const scroll = (direction) => {
    const container = scrollContainer.current;
    const scrollAmount = direction === 'left' ? -300 : 300;
    container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  };

  return (
    <Box sx={{ position: 'relative', marginTop: '20px' }}>
      <IconButton
        onClick={() => scroll('left')}
        sx={{
          position: 'absolute',
          left: -20,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
          backgroundColor: themeColors.white,
          boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
        }}
      >
        <ArrowBackIosIcon />
      </IconButton>

      <Box
        ref={scrollContainer}
        sx={{
          display: 'flex',
          overflowX: 'auto',
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': { display: 'none' },
          padding: '10px',
        }}
      >
        {reviews.map((review, index) => (
          <ReviewCard key={index} review={review} />
        ))}
      </Box>

      <IconButton
        onClick={() => scroll('right')}
        sx={{
          position: 'absolute',
          right: -20,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
          backgroundColor: themeColors.white,
          boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
        }}
      >
        <ArrowForwardIosIcon />
      </IconButton>
    </Box>
  );
};


export default HomeSection;